import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { map, pathOr } from 'ramda'
import React from 'react'
import SMSHelper from 'smshelper'

import PhoneNumberField from '@/app/component/atom/form/phone-number-field'
import Select from '@/app/component/atom/form/select'
import IconText from '@/app/component/layout/icon-text'
import Message from '@/app/component/send/message'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import { DEFAULT_SENDER_ID_MESSAGE, DEFAULT_SENDER_ID_VALUE } from '@/app/definitions'
import SMSLanguageWrapper from '@/app/module/campaigns/component/item/steps/content/language-wrapper'
import { getSMSLanguageMap } from '@/app/module/campaigns/component/item/steps/content/types/multimessage/content'
import { displayMap, Q_ACTIONS, smsMaxLength } from '@/app/module/campaigns/definitions'
import { hasContent, LanguageType } from '@/app/module/campaigns/helpers'
import { LocalSendSMSToPhoneNumbersAction } from '@/app/module/campaigns/types'
import { PhoneNumber } from '@/app/module/phonenumbers/types'
import { PersonalizationType, SelectOption } from '@/app/types'

import { getSmsSenderIds } from './sms-sender-ids'

type Props = {
  defaultLanguage: string
  defaultSenderId?: SelectOption<string>
  isNewAction: boolean
  languages: LanguageType[]
  phoneNumber?: PhoneNumber
  senderIds: SelectOption<string>[]
  personalizationList: PersonalizationType
  value: LocalSendSMSToPhoneNumbersAction
  onClose: () => void
  onSave: (value: Partial<LocalSendSMSToPhoneNumbersAction>) => void
}

type ContentSMS = Pick<LocalSendSMSToPhoneNumbersAction['sendSMSToPhoneNumbers'], 'text' | 'translations'>

const SendSMSToPhoneNumbers = (props: Props) => {
  const {
    text,
    translations,
    phoneNumbers: initialPhoneNumbers,
    senderId: initialSenderId,
    useCampaignSenderId,
  } = props.value.sendSMSToPhoneNumbers
  const [phoneNumbers, setPhoneNumbers] = React.useState(initialPhoneNumbers)
  const [message, setMessage] = React.useState<ContentSMS>({
    text,
    translations,
  })
  const [senderId, setSenderId] = React.useState(initialSenderId)
  const senderIds = React.useMemo(
    () => getSmsSenderIds(props.defaultSenderId, props.senderIds),
    [props.defaultSenderId, props.senderIds],
  )
  const { onClose, onSave } = props

  React.useEffect(() => {
    if (props.defaultSenderId && useCampaignSenderId !== 'action') {
      setSenderId(props.defaultSenderId.value)
    } else if (props.isNewAction) {
      // if there's no default sender id, and it's a new action, we need to set a default sender id
      const pn = props.phoneNumber
      setSenderId(pn && pn.sms ? pn.phoneNumber : DEFAULT_SENDER_ID_VALUE)
    } else {
      setSenderId(initialSenderId === '' ? DEFAULT_SENDER_ID_VALUE : initialSenderId)
    }
  }, [initialSenderId, props.defaultSenderId, props.isNewAction, props.phoneNumber, useCampaignSenderId])

  const { personalizationList = {}, defaultSenderId } = props

  const parts = SMSHelper.parts(message.text)

  const changeHandler = ({ message: updatedMessage }: { message: ContentSMS }) => {
    setMessage(updatedMessage)
  }

  const languageContentMap = React.useMemo(
    () => getSMSLanguageMap(message, props.defaultLanguage),
    [message, props.defaultLanguage],
  )

  return (
    <CustomDialog open={true} onClose={onClose}>
      <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <IconText>
            <Icon>{displayMap.sendSMSToPhoneNumbers.icon}</Icon>
            <Typography variant="h6">{Q_ACTIONS.sendSMSToPhoneNumbers.label}</Typography>
          </IconText>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            width: '80vw',
            minWidth: '300px',
            maxWidth: '100%',
          }}
        >
          <form
            id="send-message-form"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <div
              style={{
                marginBottom: '15px',
              }}
            >
              <PhoneNumberField
                personalizationList={personalizationList}
                value={phoneNumbers}
                onChange={setPhoneNumbers}
              />
            </div>
            <Select
              id="send-message-sender"
              label="From"
              name="senderId"
              value={senderId}
              values={senderIds}
              editable={true}
              onChange={({ value }) => {
                setSenderId(value)
              }}
              info={senderId === DEFAULT_SENDER_ID_VALUE && senderIds.length === 1 ? DEFAULT_SENDER_ID_MESSAGE : ''}
            />
            <SMSLanguageWrapper
              content="text"
              defaultLanguage={props.defaultLanguage}
              info="Add your message below"
              item={{ message }}
              languageContentMap={languageContentMap}
              languages={props.languages}
              type="message"
              onChange={changeHandler}
            >
              {(activeText: string, onTextChange: (text: string) => void, activeError?: string) => (
                <Message
                  autoFocus
                  message={activeText}
                  error={activeError}
                  personalizationList={personalizationList}
                  onChange={onTextChange}
                />
              )}
            </SMSLanguageWrapper>
          </form>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !hasContent(message, 'text') || phoneNumbers.length === 0 || senderId === '0' || parts > smsMaxLength
          }
          onClick={() =>
            onSave({
              sendSMSToPhoneNumbers: {
                senderId:
                  senderId === DEFAULT_SENDER_ID_VALUE || senderId === pathOr('', ['value'], defaultSenderId)
                    ? ''
                    : senderId,
                useCampaignSenderId:
                  defaultSenderId && senderId === pathOr('', ['value'], defaultSenderId)
                    ? defaultSenderId.value
                    : 'action',
                text: message.text,
                translations: message.translations,
                phoneNumbers: trimmedPhoneNumbers(phoneNumbers),
              },
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </CustomDialog>
  )
}

const trimmedPhoneNumbers = (phoneNumbers: string[]) =>
  map((phoneNumber) => (phoneNumber.charAt(0) === '+' ? phoneNumber.substr(1) : phoneNumber), phoneNumbers)

export default SendSMSToPhoneNumbers
